import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Home, LogOut, FileText, Wrench, CreditCard, Phone } from 'lucide-react';

export default function TenantDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-stone-50 pt-20" data-testid="tenant-dashboard">
      <div className="bg-white border-b border-stone-200 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-14">
          <div className="flex items-center gap-4">
            <h1 className="font-outfit font-bold text-stone-900 text-lg">Tenant Portal</h1>
            <Badge className="bg-emerald-100 text-emerald-800 text-xs">Tenant</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="tenant-home-btn">
              <Home className="w-4 h-4 mr-1" /> Home
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="tenant-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="font-outfit text-2xl font-bold text-stone-900">Welcome, {user?.name || 'Tenant'}</h2>
          <p className="text-stone-600 mt-1">Manage your rental information and requests from here.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="tenant-lease-card">
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-emerald-900" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 text-lg mb-2">Lease Information</h3>
            <p className="text-stone-600 text-sm">View your current lease details and documents.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="tenant-maintenance-card">
            <div className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center mb-4">
              <Wrench className="w-6 h-6 text-amber-700" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 text-lg mb-2">Maintenance Requests</h3>
            <p className="text-stone-600 text-sm">Submit and track maintenance requests for your unit.</p>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-6 shadow-sm hover:shadow-md transition-shadow" data-testid="tenant-payments-card">
            <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-4">
              <CreditCard className="w-6 h-6 text-blue-700" />
            </div>
            <h3 className="font-outfit font-semibold text-stone-900 text-lg mb-2">Payments</h3>
            <p className="text-stone-600 text-sm">View payment history and make rent payments.</p>
          </div>
        </div>

        <div className="mt-10 bg-white rounded-2xl border border-stone-200 p-8 shadow-sm" data-testid="tenant-contact-section">
          <h3 className="font-outfit font-semibold text-stone-900 text-lg mb-4">Need Help?</h3>
          <p className="text-stone-600 text-sm mb-4">Contact our property management team for any questions or concerns.</p>
          <div className="flex flex-wrap gap-4">
            <a href="tel:1-888-419-9767" className="flex items-center gap-2 text-emerald-900 font-medium text-sm hover:underline">
              <Phone className="w-4 h-4" /> 1-888-419-9767
            </a>
            <a href="mailto:general@4longevity.live" className="text-emerald-900 font-medium text-sm hover:underline">general@4longevity.live</a>
          </div>
        </div>
      </div>
    </div>
  );
}
